/**
 * Shared impersonation hook -- provider-agnostic.
 *
 * Encapsulates impersonation state, localStorage persistence, and
 * session lifecycle so every AuthProvider can reuse the same logic.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { ImpersonationManager } from '../../utils/impersonationManager';
import { User, ImpersonationContext as ImpCtx } from './types';

const INITIAL_STATE: ImpCtx = { isImpersonating: false };

export function useImpersonation(user: User | null, isAdmin: boolean) {
  const [impersonationContext, setImpersonationContext] = useState<ImpCtx>(INITIAL_STATE);
  const hasRestored = useRef(false);
  const hadUser = useRef(false);

  // Restore impersonation state once after user and admin flag both load
  useEffect(() => {
    if (!user) {
      if (hadUser.current) {
        // User was previously set and is now null → explicit or implicit logout.
        // Safe to clear the persisted session.
        ImpersonationManager.clearSession();
      }
      // On initial mount user is null before auth loads; don't touch localStorage.
      hadUser.current = false;
      hasRestored.current = false;
      setImpersonationContext(INITIAL_STATE);
      return;
    }

    hadUser.current = true;

    if (hasRestored.current) return;

    const stored = ImpersonationManager.getCurrentState();
    if (stored.isImpersonating && isAdmin) {
      setImpersonationContext(stored);
      hasRestored.current = true;
    } else if (!stored.isImpersonating) {
      hasRestored.current = true;
    }
  }, [user, isAdmin]);

  const startImpersonation = useCallback(
    (targetUser: User) => {
      if (!user) return;

      const adminOrgId = localStorage.getItem('kolega_current_organization_id');
      localStorage.removeItem('kolega_current_organization_id');
      if (adminOrgId) localStorage.setItem('kolega_admin_organization_id', adminOrgId);

      const session = ImpersonationManager.createSession(targetUser, user);
      setImpersonationContext({
        isImpersonating: true,
        targetUser,
        originalAdminUser: user,
        expiresAt: session.expiresAt,
      });

      window.location.href = '/repositories';
    },
    [user],
  );

  const stopImpersonation = useCallback(() => {
    ImpersonationManager.clearSession();
    const adminOrgId = localStorage.getItem('kolega_admin_organization_id');
    if (adminOrgId) {
      localStorage.setItem('kolega_current_organization_id', adminOrgId);
      localStorage.removeItem('kolega_admin_organization_id');
    }
    setImpersonationContext(INITIAL_STATE);
    window.location.href = '/admin';
  }, []);

  const getImpersonationTimeRemaining = useCallback((): number => {
    if (!impersonationContext.isImpersonating || !impersonationContext.expiresAt) return 0;
    return Math.max(0, impersonationContext.expiresAt - Date.now());
  }, [impersonationContext]);

  return {
    impersonationContext,
    startImpersonation,
    stopImpersonation,
    getImpersonationTimeRemaining,
  };
}
